import React from 'react';
import styled from 'styled-components';
import {
  YellowCard,
  RedCard,
  TwoYellowCards,
  SecondYellowCards,
  InjuredDisc,
  CaptainDiscSvg,
  StatusDiscSvg,
  GlyphThumbsUp,
  GlyphThumbsDown,
  GlyphLoanIn,
  GlyphLoanOut,
  GlyphDollar,
  GlyphOffer,
  GlyphAcademy,
  GoalBall,
  SubstitutionMark,
} from './icons';
import { SUSPENDED_HINT } from '../../service/matchEngine';

const MarkHost = styled.span`
  width: ${({ $size }) => $size || 20}px;
  height: ${({ $size }) => $size || 20}px;
  flex-shrink: 0;
  display: inline-grid;
  place-items: center;
  line-height: 0;
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const Stack = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  line-height: 0;
`;

const Event = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: 600 11px Archivo, sans-serif;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme?.textMuted || '#666'};
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
  .scorer {
    font-weight: 600;
    color: ${({ theme }) => theme?.text || '#222'};
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .minute {
    flex-shrink: 0;
    font-weight: 600;
    color: ${({ theme }) => theme?.textFaint || '#888'};
  }
`;

const shortScorerName = (name) => {
  if (!name) return null;
  const trimmed = String(name).trim();
  if (trimmed.length <= 16) return trimmed;
  const parts = trimmed.split(/\s+/);
  return parts[parts.length - 1] || trimmed;
};

/** Captain letter mark — only place we use a letter. Black disc, white C (path). */
export function CaptainMark({ auto = false, size = 20, title }) {
  const px = Math.max(14, Math.round(size / 2) * 2);
  return (
    <MarkHost
      $size={px}
      title={title || (auto ? 'Captain (auto)' : 'Captain')}
      aria-label={auto ? 'Captain, automatic' : 'Captain'}
    >
      <CaptainDiscSvg size={px} auto={auto} />
    </MarkHost>
  );
}

export function InjuredMark({ size = 20, title = 'Injured' }) {
  const px = Math.max(14, Math.round(size / 2) * 2);
  return (
    <MarkHost $size={px} title={title} aria-label="Injured">
      <InjuredDisc size={px} />
    </MarkHost>
  );
}

/** View Squad / transfer status disc — Lucide glyph baked into one SVG. */
function SquadStatusMark({ size = 20, bg, title, label, children }) {
  const px = Math.max(14, Math.round(size / 2) * 2);
  return (
    <MarkHost $size={px} title={title} aria-label={label || title}>
      <StatusDiscSvg size={px} bg={bg}>
        {children}
      </StatusDiscSvg>
    </MarkHost>
  );
}

export function HappyMark({ size = 20, title = 'Happy at the club' }) {
  return (
    <SquadStatusMark size={size} bg="#00996b" title={title} label="Happy">
      <GlyphThumbsUp />
    </SquadStatusMark>
  );
}

export function UnhappyMark({ size = 20, title = 'Unhappy at the club' }) {
  return (
    <SquadStatusMark size={size} bg="#cc392d" title={title} label="Unhappy">
      <GlyphThumbsDown />
    </SquadStatusMark>
  );
}

export function LoanInMark({ size = 20, title = 'Loaned in' }) {
  return (
    <SquadStatusMark size={size} bg="#0ea5e9" title={title} label="Loaned in">
      <GlyphLoanIn />
    </SquadStatusMark>
  );
}

export function LoanOutMark({ size = 20, title = 'Loaned out' }) {
  return (
    <SquadStatusMark size={size} bg="#6b7280" title={title} label="Loaned out">
      <GlyphLoanOut />
    </SquadStatusMark>
  );
}

export function ListedMark({ size = 20, title = 'Listed for sale' }) {
  return (
    <SquadStatusMark size={size} bg="#f0a500" title={title} label="Listed">
      <GlyphDollar />
    </SquadStatusMark>
  );
}

export function ListedLoanMark({ size = 20, title = 'Listed for loan' }) {
  return (
    <SquadStatusMark size={size} bg="#6366f1" title={title} label="Listed for loan">
      <GlyphLoanOut />
    </SquadStatusMark>
  );
}

export function OfferMark({ size = 20, title = 'Transfer offer' }) {
  return (
    <SquadStatusMark size={size} bg="#3b82f6" title={title} label="Offer">
      <GlyphOffer />
    </SquadStatusMark>
  );
}

export function AcademyMark({ size = 20, title = 'Academy' }) {
  return (
    <SquadStatusMark size={size} bg="#00996b" title={title} label="Academy">
      <GlyphAcademy />
    </SquadStatusMark>
  );
}

/** Season yellow count as card art (1 or 2). */
export function YellowCountMark({ count = 1, size = 14, title }) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  const label =
    title ||
    `${n} yellow card${n > 1 ? 's' : ''} this season`;
  return (
    <Stack title={label} aria-label={label}>
      {n >= 2 ? <TwoYellowCards size={size} /> : <YellowCard size={size} />}
    </Stack>
  );
}

/** Ban / suspension — red card (no letter). */
export function SuspendedMark({ size = 14, title = SUSPENDED_HINT }) {
  return (
    <Stack title={title} aria-label="Suspended">
      <RedCard size={size} />
    </Stack>
  );
}

/**
 * Sent off in a live match.
 * secondYellow → yellow+red; otherwise straight red.
 */
export function SentOffMark({ secondYellow = false, size = 14, title }) {
  const label =
    title ||
    (secondYellow ? 'Sent off (second yellow)' : 'Sent off');
  return (
    <Stack title={label} aria-label={label}>
      {secondYellow ? (
        <SecondYellowCards size={size} />
      ) : (
        <RedCard size={size} />
      )}
    </Stack>
  );
}

/** Pitch / token cluster: injured, suspended, yellows, sent-off, captain. */
export function PitchStatusMarks({
  injured,
  suspended,
  yellowCards = 0,
  sentOff,
  sentOffSecondYellow,
  isCaptain,
  captainAuto,
  inline = false,
  size,
}) {
  if (
    !injured &&
    !suspended &&
    !sentOff &&
    !(yellowCards > 0) &&
    !isCaptain
  ) {
    return null;
  }
  const well = size || (inline ? 16 : 18);
  const card = size ? Math.round(size * 0.72) : inline ? 12 : 13;
  return (
    <Stack
      style={
        inline
          ? { gap: 4 }
          : { flexDirection: 'column', alignItems: 'flex-end', gap: 2 }
      }
    >
      {injured && <InjuredMark size={well} />}
      {suspended && <SuspendedMark size={card} />}
      {sentOff && (
        <SentOffMark secondYellow={!!sentOffSecondYellow} size={card} />
      )}
      {!sentOff && yellowCards > 0 && (
        <YellowCountMark count={yellowCards} size={card} />
      )}
      {isCaptain && <CaptainMark auto={captainAuto} size={well} />}
    </Stack>
  );
}

/** Live / report event: icon + minute(s) + scorer (grouped braces: ⚽ 23', 28' Name). */
export function MatchEventMark({
  kind,
  minute,
  minutes,
  secondYellow,
  playerName,
  playerInName,
  playerOutName,
}) {
  const minuteList =
    Array.isArray(minutes) && minutes.length
      ? minutes
      : minute != null
        ? [minute]
        : [];
  const minParts = minuteList
    .filter((m) => m != null && Number.isFinite(Number(m)))
    .map((m) => `${Number(m)}'`);
  const minLabel = minParts.length ? minParts.join(', ') : null;
  let icon = null;
  let label = playerName || 'Event';
  let scorer = shortScorerName(playerName);
  if (kind === 'goal') {
    icon = <GoalBall size={12} />;
    label = `${playerName || 'Goal'}${minLabel ? ` · ${minLabel}` : ''}`;
  } else if (kind === 'yellow') {
    icon = <YellowCard size={12} />;
    label = `${playerName || 'Yellow'}${minLabel ? ` · ${minLabel}` : ''}`;
  } else if (kind === 'red') {
    icon = secondYellow ? (
      <SecondYellowCards size={12} />
    ) : (
      <RedCard size={12} />
    );
    label = `${playerName || 'Red'}${minLabel ? ` · ${minLabel}` : ''}${
      secondYellow ? ' (2nd yellow)' : ''
    }`;
  } else if (kind === 'sub') {
    icon = <SubstitutionMark size={12} />;
    const inShort = shortScorerName(playerInName);
    const outShort = shortScorerName(playerOutName);
    if (inShort && outShort) {
      scorer = `${inShort} for ${outShort}`;
      label = `${playerInName} for ${playerOutName}${
        minLabel ? ` · ${minLabel}` : ''
      }`;
    } else {
      scorer = shortScorerName(playerName) || playerName || 'Sub';
      label = `${playerName || 'Substitution'}${
        minLabel ? ` · ${minLabel}` : ''
      }`;
    }
  } else {
    return null;
  }
  return (
    <Event title={label} aria-label={label}>
      {icon}
      {minLabel ? <span className="minute">{minLabel}</span> : null}
      {scorer ? <span className="scorer">{scorer}</span> : null}
    </Event>
  );
}
